import { defaultLaunchMessageWhenEmpty } from "@/lib/launch/composeLaunchUserMessage";
import { uploadProjectDocumentsParallel } from "@/lib/documents/uploadProjectDocumentsParallel";
import { useLaunchHandoffStore } from "@/stores/useLaunchHandoffStore";
import { usePendingConversationPromptStore } from "@/stores/usePendingConversationPromptStore";
import { useProjectDashboardStore } from "@/stores/useProjectDashboardStore";
import { getErrorMessage } from "@/types/problem-detail";
import { toast } from "sonner";

/**
 * Clears a files-only launch stash or drops pending uploads while keeping user-typed text.
 */
function finalizeLaunchAfterFailedUploads(conversationId: string): void {
  const { peekPrompt, takePrompt, patchPrompt } =
    usePendingConversationPromptStore.getState();
  const stash = peekPrompt(conversationId);
  if (!stash) return;

  const attachmentOnlyMessage = defaultLaunchMessageWhenEmpty(
    true,
    stash.contextMentions.length > 0,
  );
  if (stash.text.trim() === attachmentOnlyMessage.trim()) {
    takePrompt(conversationId);
    return;
  }

  patchPrompt(conversationId, { documentsPending: false, documents: [] });
}

export type CompleteLaunchAfterNavigateArgs = {
  projectId: string;
  conversationId: string;
  files: File[];
};

/**
 * Finishes document uploads after navigating to the workbench so launch
 * orchestration survives ProjectHomeIntentField unmount.
 */
export async function completeLaunchAfterNavigate({
  projectId,
  conversationId,
  files,
}: CompleteLaunchAfterNavigateArgs): Promise<void> {
  if (files.length === 0) return;

  const { updateSurrogate, abort } = useLaunchHandoffStore.getState();
  const { patchPrompt } = usePendingConversationPromptStore.getState();
  const loadAnalytics = useProjectDashboardStore.getState().loadAnalytics;

  updateSurrogate({ uploading: true, fileNames: files.map((f) => f.name) });

  try {
    const { uploaded, failed } = await uploadProjectDocumentsParallel(
      projectId,
      files,
      (fileName, progress) => {
        updateSurrogate({
          uploadProgress: {
            ...useLaunchHandoffStore.getState().surrogate.uploadProgress,
            [fileName]: progress,
          },
        });
      },
    );

    updateSurrogate({ uploading: false });

    if (failed.length > 0) {
      const firstReason =
        failed[0]?.error instanceof Error ? failed[0].error.message : "Upload failed";
      toast.error(
        failed.length === files.length
          ? `Couldn't upload the selected files. ${firstReason}`
          : `${failed.length} of ${files.length} files failed to upload. ${firstReason}`,
      );
    }

    if (uploaded.length === 0) {
      abort();
      finalizeLaunchAfterFailedUploads(conversationId);
      return;
    }

    patchPrompt(conversationId, {
      documents: uploaded,
      documentsPending: false,
    });
    void loadAnalytics(projectId, { force: true });
  } catch (e) {
    updateSurrogate({ uploading: false });
    abort();
    finalizeLaunchAfterFailedUploads(conversationId);
    toast.error(getErrorMessage(e, "Couldn't upload documents for this conversation."));
  }
}
